import PropTypes from 'prop-types';
import React from 'react';
import Form from 'react-validation/build/form';

import SelectButtonGroup from 'atoms/SelectButtonGroup';
import TextInput from 'atoms/TextInput';
import GeoDropdown from 'components/GeoDropdown';
import { generateObjectArray } from 'utils/helpers';
import { required } from 'utils/validations';

/**
 * PatientAddress - Stateless controlled component
 * All state is managed by parent (PatientRegister)
 */

const isGeoComplete = (geo) => geo.state && geo.city && geo.zipcode;

const PatientAddress = ({
	address,
	states,
	cities,
	zipcodes,
	onFieldChange,
	onGeoChange,
	onNext,
	onPrev,
}) => {
	const { primaryAddress, mailingAddress, livingAddress } = address;
	const requiresMailing = address.sendStatement === 'no';
	const requiresLiving = address.livingAtPrimaryAddress === 'no';

	const handleGeoSelect = (group) => (type) => (data) => {
		// Clear dependent fields when parent field changes
		if (type === 'state') {
			onFieldChange(`address.${group}.city`)(null);
			onFieldChange(`address.${group}.zipcode`)(null);
		} else if (type === 'city') {
			onFieldChange(`address.${group}.zipcode`)(null);
		}

		onFieldChange(`address.${group}.${type}`)(data);
		onGeoChange(type, data);
	};

	// trim: the old react-validation path rejected whitespace-only streets via
	// the required validator's trim; bare truthiness would re-allow "   "
	const hasStreet = (street) => Boolean(street && street.trim());
	const isPrimaryValid =
		hasStreet(address.street) && isGeoComplete(primaryAddress);
	const isMailingValid =
		!requiresMailing ||
		(hasStreet(address.mailingAddressStreet) && isGeoComplete(mailingAddress));
	const isLivingValid =
		!requiresLiving ||
		(hasStreet(address.livingAddressStreet) && isGeoComplete(livingAddress));
	const isValid = isPrimaryValid && isMailingValid && isLivingValid;

	return (
		<Form>
			<div className="form-group">
				<TextInput
					label="STREET ADDRESS*"
					id="street"
					value={address.street || ''}
					placeholder="110, Old Hill Road"
					handleInput={(e) => onFieldChange('address.street')(e.target.value)}
					validations={[required]}
				/>
			</div>

			<GeoDropdown
				states={states}
				cities={
					primaryAddress.state ? cities[primaryAddress.state.value] : []
				}
				zipcodes={
					primaryAddress.city ? zipcodes[primaryAddress.city.value] : []
				}
				handleChange={handleGeoSelect('primaryAddress')}
				state={primaryAddress.state}
				city={primaryAddress.city}
				zipcode={primaryAddress.zipcode}
				isRequired
			/>

			<div className="form-row">
				<div className="col-md-6 form-group">
					<label htmlFor="sendStatement" className="form-font">
						SEND STATEMENT TO THE SAME ADDRESS?
					</label>
					<SelectButtonGroup
						id="sendStatement"
						value={address.sendStatement}
						buttons={generateObjectArray(['Yes', 'No'])}
						handleSelect={(e) =>
							onFieldChange('address.sendStatement')(e.target.value)
						}
					/>
				</div>
			</div>

			{address.sendStatement === 'no' && (
				<div>
					<div className="form-group">
						<label htmlFor="mailingAddressStreet" className="form-font mt-4">
							SEND STATEMENT TO: MAILING ADDRESS*
						</label>
						<input
							value={address.mailingAddressStreet || ''}
							placeholder="119, Old Hyde Road"
							type="text"
							name="mailingAddressStreet"
							className="form-control"
							id="mailingAddressStreet"
							onChange={(e) =>
								onFieldChange('address.mailingAddressStreet')(e.target.value)
							}
						/>
					</div>
					<GeoDropdown
						states={states}
						cities={
							mailingAddress.state ? cities[mailingAddress.state.value] : []
						}
						zipcodes={
							mailingAddress.city ? zipcodes[mailingAddress.city.value] : []
						}
						handleChange={handleGeoSelect('mailingAddress')}
						state={mailingAddress.state}
						city={mailingAddress.city}
						zipcode={mailingAddress.zipcode}
						isRequired
					/>
				</div>
			)}

			<div className="form-row">
				<div className="col-md-6 form-group">
					<label htmlFor="livingAtPrimaryAddress" className="form-font">
						WILL YOU BE RESIDING AT THIS ADDRESS DURING YOUR TREATMENT?
					</label>
					<SelectButtonGroup
						id="livingAtPrimaryAddress"
						value={address.livingAtPrimaryAddress}
						buttons={generateObjectArray(['Yes', 'No'])}
						handleSelect={(e) =>
							onFieldChange('address.livingAtPrimaryAddress')(e.target.value)
						}
					/>
				</div>
			</div>

			{address.livingAtPrimaryAddress === 'no' && (
				<div>
					<div className="form-group">
						<label htmlFor="livingAddressStreet" className="form-font mt-4">
							RESIDENCE DURING TREATMENT AT MIND THERAPY CLINIC*
						</label>
						<input
							value={address.livingAddressStreet || ''}
							placeholder="119, Old Hyde Road"
							type="text"
							name="livingAddressStreet"
							className="form-control"
							id="livingAddressStreet"
							onChange={(e) =>
								onFieldChange('address.livingAddressStreet')(e.target.value)
							}
						/>
					</div>
					<GeoDropdown
						states={states}
						cities={
							livingAddress.state ? cities[livingAddress.state.value] : []
						}
						zipcodes={
							livingAddress.city ? zipcodes[livingAddress.city.value] : []
						}
						handleChange={handleGeoSelect('livingAddress')}
						state={livingAddress.state}
						city={livingAddress.city}
						zipcode={livingAddress.zipcode}
						isRequired
					/>
				</div>
			)}

			<div className="d-flex mt-3">
				<div className="col-6 pl-0">
					<button
						type="button"
						className="btn btn-white link-color border btn-lg btn-block font-weight-bold d-flex justify-content-start font-15"
						onClick={onPrev}
					>
						<div className="col-4 d-flex justify-content-start align-self-center">
							<img className="fas fa-arrow-left" alt="arrow-left" />
						</div>
						<div className="col-4 d-flex justify-content-center">
							<span>PREV</span>
						</div>
					</button>
				</div>
				<div className="col-6 p-0 d-flex">
					<button
						type="button"
						disabled={!isValid}
						className="btn btn-primary btn-color btn-lg btn-block font-weight-bold d-flex justify-content-end font-15"
						onClick={onNext}
					>
						<div className="col-4 d-flex justify-content-center">
							<span>NEXT</span>
						</div>
						<div className="col-4 d-flex justify-content-end align-self-center">
							<img className="fas fa-arrow-right" alt="arrow-right" />
						</div>
					</button>
				</div>
			</div>
		</Form>
	);
};

PatientAddress.propTypes = {
	address: PropTypes.shape({
		street: PropTypes.string,
		primaryAddress: PropTypes.object,
		mailingAddress: PropTypes.object,
		livingAddress: PropTypes.object,
		sendStatement: PropTypes.string,
		livingAtPrimaryAddress: PropTypes.string,
		mailingAddressStreet: PropTypes.string,
		livingAddressStreet: PropTypes.string,
	}).isRequired,
	states: PropTypes.array,
	cities: PropTypes.object,
	zipcodes: PropTypes.object,
	onFieldChange: PropTypes.func.isRequired,
	onGeoChange: PropTypes.func.isRequired,
	onNext: PropTypes.func.isRequired,
	onPrev: PropTypes.func.isRequired,
};

export default PatientAddress;
